import { useParams } from "react-router-dom";
import Table from "../../Components/Table";
import useTableControls from "../../Hooks/useTableControls";
import { useQuery, useMutation } from "react-query";
import { getUserDetails } from "../../Services/User";
// Disabled: vehicle approve/reject — use Request Re-submit / Signup Requests instead
// import { changeVehicleRequestStatus } from "../../Services/VehicleRequest";
import { requestDriverResubmit } from "../../Services/SignupRequest";
import { RejectDriverSignup, getExpiryFieldsToFix } from "../../Components/Modal/RejectDriverSignup";
import { useEffect, useState } from "react";
import useFetchData from "../../Hooks/useFetchData";
import { getAllRides } from "../../Services/Ride";
import { format_date, formatDriverLocationUpdatedAt, getDriverCoords, getSerialNumber, resolveImageUrl } from "../../Utils/helpers";
import BackButton from "../../Components/BackButton";
import StatusHandler from "../../Components/Table/StatusHandler";
import OpenSupportChatButton from "../../Components/OpenSupportChatButton";
import ModalImageViewer from "../../Components/ModalImageViewer";
import Button from "../../Components/Button";
import { Error, Success } from "../../Components/Modal";
import DocumentExpiryEditor from "../../Components/DocumentExpiryEditor";
import "../Dashboard/Dashboard.css";
import { formatBookingCode } from "../../Utils/bookingCode";
import AccountDeletionBadge, { isAccountDeleted } from "../../Components/AccountDeletionBadge";
import AccountDeletionActions from "../../Components/AccountDeletionActions";

const AVATAR_FALLBACK = "images/avatar.png";

const AvailabilityBadge = ({ online, size = "md" }) => (
    <span
        className={`driver-availability-badge driver-availability-badge--${size} ${
            online ? "is-online" : "is-offline"
        }`}
    >
        <span className="driver-availability-dot" aria-hidden="true" />
        {online ? "Online" : "Offline"}
    </span>
);

const getVehicleStatusMeta = (status, reSubmit = false) => {
    const value = status?.toLowerCase();
    if (value === "approved") {
        return { label: "Approved", pillClass: "status-active", tagClass: "green-tag" };
    }
    if (value === "rejected" || value === "cancelled") {
        return { label: value === "cancelled" ? "Cancelled" : "Rejected", pillClass: "status-rejected", tagClass: "red-tag" };
    }
    if (value === "pending") {
        if (reSubmit) {
            return { label: "Re-submitted", pillClass: "status-pending", tagClass: "orange-tag" };
        }
        return { label: "Pending", pillClass: "status-pending", tagClass: "orange-tag" };
    }
    return { label: "Not Submitted", pillClass: "status-inactive", tagClass: "orange-tag" };
};

export default function MoverDetails() {
    const { id } = useParams();
    const [driver, setDriver] = useState({});
    const [profileImageSrc, setProfileImageSrc] = useState(AVATAR_FALLBACK);
    const {
        perPage,
        setPerPage,
        status,
        setStatus,
        search_string,
        setSearchString,
        from,
        setFrom,
        to,
        setTo,
    } = useTableControls();

    const { data: driverData, refetch: refetchDriver } = useQuery(["driver_details", id], () =>
        getUserDetails(id),
        { refetchInterval: 15000 }
    );

    useEffect(() => {
        if (driverData?.data?.data?.docs?.length > 0)
            setDriver(driverData?.data?.data?.docs?.[0]);
    }, [driverData]);

    useEffect(() => {
        setProfileImageSrc(resolveImageUrl(driver?.profileImage, AVATAR_FALLBACK));
    }, [driver?.profileImage]);

    const [viewerShow, setViewerShow] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);
    const [viewerTitle, setViewerTitle] = useState("");

    const openImageViewer = (imagesList, index = 0, title = "Image Viewer") => {
        setViewerImages(imagesList);
        setViewerIndex(index);
        setViewerTitle(title);
        setViewerShow(true);
    };

    const {
        page,
        setPage,
        isFetching: rideFetching,
        isLoading: rideLoading,
        data: rideData,
    } = useFetchData("driver_ride_logs", getAllRides, [
        perPage,
        search_string,
        status,
        from,
        to,
        null,
        id,
    ]);

    const fullNameStr = [driver?.firstName, driver?.lastName].filter(Boolean).join(" ");
    const fullName = fullNameStr || <span className="text-muted" style={{ fontSize: '1.5rem', fontWeight: 500 }}>Profile Incomplete</span>;
    const totalBookings = rideData?.data?.data?.totalDocs || 0;
    const memberSince = driver?.createdAt ? format_date(driver.createdAt, "MMM YYYY") : null;
    const { lat, lng, hasValidCoords } = getDriverCoords(driver?.location);
    const mapsUrl = hasValidCoords ? `https://www.google.com/maps?q=${lat},${lng}` : null;
    const licenseFrontUrl = resolveImageUrl(driver?.licenseImageFront);
    const licenseBackUrl = resolveImageUrl(driver?.licenseImageBack);
    const insuranceUrl = resolveImageUrl(driver?.insurance);
    const vehicle = driver?.vehicle;
    const vehicleImages = (vehicle?.vehicleImage || [])
        .map((item) => resolveImageUrl(item))
        .filter(Boolean);

    const profileStats = [
        { label: "Phone Number", value: driver?.phone || "N/A" },
        { label: "Email Address", value: driver?.email || <span className="text-muted" style={{ fontSize: '0.85rem' }}>Profile Incomplete</span> },
        {
            label: "Availability",
            value: <AvailabilityBadge online={!!driver?.online} size="sm" />,
        },
        {
            label: "Latitude",
            value: hasValidCoords ? (
                mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="driver-location-link">
                        {lat.toFixed(6)}
                    </a>
                ) : (
                    lat.toFixed(6)
                )
            ) : "N/A",
        },
        {
            label: "Longitude",
            value: hasValidCoords ? (
                mapsUrl ? (
                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="driver-location-link">
                        {lng.toFixed(6)}
                    </a>
                ) : (
                    lng.toFixed(6)
                )
            ) : "N/A",
        },
        {
            label: "Last Location Update",
            value: formatDriverLocationUpdatedAt(driver),
        },
        { label: "SSN", value: driver?.ssn || "N/A" },
        {
            label: "Date of Birth",
            value: driver?.dob ? format_date(driver.dob, "DD/MM/YYYY") : "N/A",
        },
        { label: "Zipcode", value: driver?.zipcode || "N/A" },
        { label: "License State", value: driver?.driverLicenseState || "N/A" },
        {
            label: "Date Joined",
            value: driver?.createdAt ? format_date(driver.createdAt, "DD/MM/YYYY") : "N/A",
        },
        { label: "Total Bookings", value: totalBookings, highlight: true },
    ];

    const vehicleStatusMeta = getVehicleStatusMeta(vehicle?.status, vehicle?.reSubmit);

    // Disabled: separate vehicle approve/reject
    // const { mutate: updateVehicleStatus, isLoading: vehicleStatusLoading } = useMutation(...)

    const { mutate: requestResubmit, isLoading: resubmitLoading } = useMutation(
        (payload) => requestDriverResubmit(driver?._id || id, payload),
        {
            onSuccess: (res) => {
                refetchDriver();
                Success("Request Re-submit", res.data?.message || "Driver must update flagged documents.");
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Failed to request re-submit");
            },
        }
    );

    const canRequestResubmit = Boolean(driver?.adminApproved);

    const vehicleStats = [
        { label: "Vehicle Status", value: vehicle?._id ? vehicleStatusMeta.label : "No Vehicle" },
        { label: "License Number", value: driver?.driverLicenseNumber || "N/A" },
        { label: "Vehicle Name", value: vehicle?.name || "N/A" },
        { label: "Vehicle Type", value: vehicle?.type?.name || "N/A" },
        { label: "Brand Name", value: vehicle?.brandName || "N/A" },
        { label: "Model Year", value: vehicle?.year || "N/A" },
        { label: "Plate Number", value: vehicle?.numberPlate || "N/A" },
        { label: "Color", value: vehicle?.color || "N/A" },
    ];

    const handleProfileImageError = () => {
        setProfileImageSrc(AVATAR_FALLBACK);
    };

    const openProfileViewer = () => {
        if (profileImageSrc && profileImageSrc !== AVATAR_FALLBACK) {
            openImageViewer([profileImageSrc], 0, "Profile Photo");
        }
    };

    return (
        <div className="app-content content dashboard">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="row">
                            <div className="col-12">
                                <div className="user-details-wrapper">
                                    <div className="row mb-4">
                                        <div className="col-12">
                                            <div className="back-title d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                <div className="d-flex align-items-center">
                                                    <BackButton />
                                                    <h1 className="pageTitle m-0 ms-3 fw-800 fs-2">Driver Details</h1>
                                                </div>
                                                <div className="back-title-actions">
                                                    {canRequestResubmit && (
                                                        <Button
                                                            type="button"
                                                            className="request-resubmit-btn"
                                                            loading={resubmitLoading}
                                                            onClick={() => {
                                                                RejectDriverSignup(
                                                                    ({ reason, rejectedFields }) => {
                                                                        requestResubmit({ reason, rejectedFields });
                                                                    },
                                                                    {
                                                                        mode: "resubmit",
                                                                        title: "Ask for document updates",
                                                                        confirmButtonText: "Send request",
                                                                        preselectedFields: getExpiryFieldsToFix({
                                                                            licenseExpiryDate: driver?.licenseExpiryDate,
                                                                            ...(vehicle ? {
                                                                                insuranceExpiryDate: vehicle.insuranceExpiryDate,
                                                                                registrationExpiryDate: vehicle.registrationExpiryDate,
                                                                            } : {}),
                                                                        }),
                                                                    }
                                                                );
                                                            }}
                                                        >
                                                            Request Re-submit
                                                        </Button>
                                                    )}
                                                    <AccountDeletionActions user={driver} label="driver account" onChanged={() => refetchDriver()} />
                                                    <OpenSupportChatButton
                                                        participantId={driver?._id || id}
                                                        chatTab="movers"
                                                        participantName={fullNameStr || "Driver"}
                                                        participantAvatar={profileImageSrc}
                                                        participantRole="Driver"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Profile Section */}
                                    <div className="row mb-4 mb-lg-5">
                                        <div className="col-12">
                                            <div className="user-profile-card details-card-premium">
                                                <div className="user-profile-banner" />
                                                <div className="user-profile-body">
                                                    <div className="user-profile-top d-flex flex-column flex-md-row align-items-center align-items-md-start gap-3 gap-md-4">
                                                        <button
                                                            type="button"
                                                            className="user-profile-avatar-btn"
                                                            onClick={openProfileViewer}
                                                            aria-label="View profile photo"
                                                        >
                                                            <div className="user-profile-avatar-wrap">
                                                                <img
                                                                    src={profileImageSrc}
                                                                    alt={fullNameStr || "Driver"}
                                                                    className="userImage"
                                                                    onError={handleProfileImageError}
                                                                />
                                                            </div>
                                                        </button>

                                                        <div className="user-profile-meta flex-grow-1 text-center text-md-start w-100">
                                                            <div className="d-flex flex-column flex-sm-row align-items-center align-items-sm-start justify-content-between gap-2 gap-sm-3 mb-2">
                                                                <div>
                                                                    <h2 className="user-profile-name text-capitalize mb-1">
                                                                        {fullName}
                                                                    </h2>
                                                                    <p className="user-profile-email mb-0">{driver?.email || <span className="text-muted" style={{ fontSize: '0.85rem' }}>Profile Incomplete</span>}</p>
                                                                    {memberSince && (
                                                                        <p className="user-profile-member mb-0">
                                                                            Member since {memberSince}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                                <div className="driver-profile-badges">
                                                                    {isAccountDeleted(driver) ? (
                                                                        <AccountDeletionBadge item={driver} className="" />
                                                                    ) : (
                                                                        <span className={`driver-account-badge ${(!driver?.firstName || !driver?.email) ? "is-incomplete" : (driver?.active ? "is-active" : "is-suspended")}`}>
                                                                            {(!driver?.firstName || !driver?.email) ? "Incomplete" : (driver?.active ? "Active" : "Inactive")}
                                                                        </span>
                                                                    )}
                                                                    {!isAccountDeleted(driver) && <AccountDeletionBadge item={driver} className="" />}
                                                                    {driver?.active && driver?.pendingSuspension ? (
                                                                        <span className="driver-account-badge is-incomplete" title="Will be suspended when the current delivery ends">
                                                                            Suspension scheduled
                                                                        </span>
                                                                    ) : null}
                                                                    <AvailabilityBadge online={!!driver?.online} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="user-profile-stats">
                                                        {profileStats.map((item) => (
                                                            <div
                                                                key={item.label}
                                                                className={`user-profile-stat ${
                                                                    item.label === "Availability" ? "user-profile-stat--availability" : ""
                                                                }`}
                                                            >
                                                                <span className="user-profile-stat-label">{item.label}</span>
                                                                <span className={`user-profile-stat-value ${item.highlight ? "is-highlight" : ""}`}>
                                                                    {item.value}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Vehicle Details */}
                                    <div className="row mb-4 mb-lg-5">
                                        <div className="col-12">
                                            <div className="details-card-premium p-0 overflow-hidden">
                                                <div className="driver-section-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                                                    <h3 className="driver-docs-title mb-0">Vehicle & License Details</h3>
                                                    {vehicle?._id && (
                                                        <span className={`status-pill ${vehicleStatusMeta.pillClass} text-uppercase fw-bold shadow-sm`}>
                                                            {vehicleStatusMeta.label}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="user-profile-body pt-0">
                                                    <div className="user-profile-stats mb-0 border-0 pt-0">
                                                        {vehicleStats.map((item) => (
                                                            <div key={item.label} className="user-profile-stat">
                                                                <span className="user-profile-stat-label">{item.label}</span>
                                                                <span className="user-profile-stat-value">{item.value}</span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div className="driver-docs-section">
                                                        <h4 className="driver-docs-subtitle">License Documents</h4>
                                                        <div className="row g-3">
                                                            <div className="col-sm-6 col-lg-4">
                                                                {licenseFrontUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer(
                                                                            [licenseFrontUrl, licenseBackUrl].filter(Boolean),
                                                                            0,
                                                                            "Driver License"
                                                                        )}
                                                                    >
                                                                        <img src={licenseFrontUrl} alt="License front" />
                                                                        <span className="driver-doc-card__label">License Front</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No front license image</div>
                                                                )}
                                                            </div>
                                                            <div className="col-sm-6 col-lg-4">
                                                                {licenseBackUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer(
                                                                            [licenseFrontUrl, licenseBackUrl].filter(Boolean),
                                                                            licenseFrontUrl ? 1 : 0,
                                                                            "Driver License"
                                                                        )}
                                                                    >
                                                                        <img src={licenseBackUrl} alt="License back" />
                                                                        <span className="driver-doc-card__label">License Back</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No back license image</div>
                                                                )}
                                                            </div>
                                                            <div className="col-sm-6 col-lg-4">
                                                                {insuranceUrl ? (
                                                                    <button
                                                                        type="button"
                                                                        className="driver-doc-card w-100 text-start"
                                                                        onClick={() => openImageViewer([insuranceUrl], 0, "Insurance Document")}
                                                                    >
                                                                        <img src={insuranceUrl} alt="Insurance" />
                                                                        <span className="driver-doc-card__label">Insurance</span>
                                                                    </button>
                                                                ) : (
                                                                    <div className="driver-doc-empty">No insurance document</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <DocumentExpiryEditor driver={driver} onSaved={() => refetchDriver()} />

                                                    <div className="driver-docs-section mb-0 pb-0">
                                                        <h4 className="driver-docs-subtitle">Vehicle Images</h4>
                                                        {vehicleImages.length > 0 ? (
                                                            <div className="driver-vehicle-images">
                                                                {vehicleImages.map((imgUrl, idx) => (
                                                                    <button
                                                                        key={imgUrl + idx}
                                                                        type="button"
                                                                        className="driver-doc-card driver-doc-card--compact"
                                                                        onClick={() => openImageViewer(vehicleImages, idx, "Vehicle Images")}
                                                                    >
                                                                        <img src={imgUrl} alt={`Vehicle ${idx + 1}`} />
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="driver-doc-empty driver-doc-empty--inline">
                                                                No vehicle images uploaded
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Disabled: vehicle approve/reject — use Request Re-submit above / Signup Requests
                                                    {vehicle?._id && vehicle?.status === "pending" && (
                                                        <div className="driver-request-actions d-flex flex-wrap gap-3 justify-content-center justify-content-md-start">
                                                            ...
                                                        </div>
                                                    )}
                                                    */}

                                                    {(vehicle?.reSubmit || vehicle?.driverNote?.trim()) && vehicle?.driverNote?.trim() && (
                                                        <div className="vehicle-resubmit-note">
                                                            <span className="vehicle-resubmit-note__label">Driver Re-Submit Message</span>
                                                            <p className="vehicle-resubmit-note__text">{vehicle.driverNote}</p>
                                                        </div>
                                                    )}

                                                    {(vehicle?.status === "rejected" || vehicle?.status === "cancelled") && vehicle?.rejectedReason && (
                                                        <div className="vehicle-rejection-note">
                                                            <span className="vehicle-rejection-note__label">Rejection Reason</span>
                                                            <p className="vehicle-rejection-note__text">{vehicle.rejectedReason}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Booking Logs Table */}
                                    <Table
                                        key={"mover-details-booking-logs-table"}
                                        tableTitles={[
                                            "S.No.",
                                            "Booking ID",
                                            "Date",
                                            "Pickup Location",
                                            "Drop-Off Location",
                                            "Total Amount",
                                            "Admin Commission",
                                            "Driver Share",
                                            "Status",
                                            "Action",
                                        ]}
                                        dateFilter
                                        showEntries
                                        sortBy
                                        isLoading={rideLoading}
                                        isFetching={rideFetching}
                                        data={rideData?.data?.data?.docs}
                                        totalPages={Math.ceil(rideData?.data?.data?.totalPages || 0)}
                                        totalDocs={rideData?.data?.data?.totalDocs}
                                        page={page}

                                        setPage={setPage}
                                        setSearchString={setSearchString}
                                        status={status}
                                        setStatus={setStatus}
                                        perPage={perPage}
                                        setPerPage={setPerPage}
                                        from={from}
                                        setFrom={setFrom}
                                        to={to}
                                        setTo={setTo}
                                        status_options={[
                                            { label: "Pending", value: "pending" },
                                            { label: "Accepted", value: "accepted" },
                                            { label: "Rejected", value: "rejected" },
                                            { label: "Started", value: "started" },
                                            { label: "Completed", value: "completed" },
                                            { label: "Cancelled", value: "cancelled" },
                                        ]}
                                    >
                                        {rideData?.data?.data?.docs?.map((item, index) => (
                                                <tr key={item?._id}>
                                                    <td>{getSerialNumber(rideData?.data?.data, index)}</td>
                                                    <td>{formatBookingCode(item)}</td>
                                                    <td>{item?.createdAt && format_date(item?.createdAt, "MM/DD/YYYY")}</td>
                                                    <td>{item?.bookingDetails?.pickupLocation?.name}</td>
                                                    <td>{item?.bookingDetails?.dropOffLocation?.name}</td>
                                                    <td className="fw-500">${item?.userTotalFare || item?.bookingDetails?.userTotalFare ? (item?.userTotalFare || item?.bookingDetails?.userTotalFare)?.toFixed(2) : (item?.bookingDetails?.price ? item?.bookingDetails?.price?.toFixed(2) : "0.00")}</td>
                                                    <td>{item?.adminCommissionAmt || item?.bookingDetails?.adminCommissionAmt ? `$${item?.adminCommissionAmt || item?.bookingDetails?.adminCommissionAmt}` : "-"}</td>
                                                    <td>{item?.driverTripFare || item?.bookingDetails?.driverTripFare ? `$${item?.driverTripFare || item?.bookingDetails?.driverTripFare}` : "-"}</td>
                                                    <td>
                                                        <span className={`status-tag-sm ${item?.status}-tag`}>
                                                            {item?.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <StatusHandler
                                                            view
                                                            item={item}
                                                            redirectUrl={`/ride-management/ride-details/${item?._id}`}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </section>

                    <ModalImageViewer
                        show={viewerShow}
                        images={viewerImages}
                        startIndex={viewerIndex}
                        onHide={() => setViewerShow(false)}
                        title={viewerTitle}
                    />
                </div>
            </div>
        </div>
    );
}
